/**
 * Expense Detail Page (UI-1 v3 layout, BE-5a/b/c, BE-19, BE-2 audit data)
 *
 * Route: /accounting/expenses/:id
 * Actions by status — DRAFT: submit / skip / edit / delete; PENDING_APPROVAL: approve / reject / edit / delete;
 * APPROVED: mark paid (unpaid bill, inline), record refund, repeat monthly, reverse; REJECTED: copy as new;
 * cheque: clear / bounce / cancel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Grid, Divider, Alert, Skeleton, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, ListSubheader, Chip, Link,
} from '@mui/material';
import {
  ArrowBack, Edit, Delete, Check, Close, Payment, Undo, Send, SkipNext, Replay, Repeat, ContentCopy,
} from '@mui/icons-material';
import { StatusChip, ReceiptAttachment, JournalEntryLink } from '../../components/accounting';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/accounting/formatters';
import { fontSize, fontWeight } from '../../utils/accounting/styles';
import { can } from '../../utils/roleUtils';
import { expenseService, decodePayFrom } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';

const today = () => new Date().toISOString().slice(0, 10);

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const SOURCE_LABEL = {
  WORK_ORDER_FINE: 'Work order fine (TC liability)',
  PARTNER_FINE: 'Partner fine',
  RECURRING_TEMPLATE: 'Monthly recurring draft',
};

const DetailRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', py: 0.75 }}>
    <Typography sx={{ fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: 'text.secondary', minWidth: 140 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: fontSize.sm }}>{children}</Typography>
  </Box>
);

const sectionTitle = { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 };
const btnSx = { textTransform: 'none', fontSize: fontSize.sm };
const shortId = (uuid) => (uuid ? String(uuid).slice(0, 8) : '');

const EntryLink = ({ id }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Link component={RouterLink} to={`/accounting/journal-entries/${id}`} underline="hover">{shortId(id)}</Link>
    <JournalEntryLink id={id} />
  </Box>
);

const ExpenseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [expense, setExpense] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payFromOptions, setPayFromOptions] = useState([]);
  const [showPayForm, setShowPayForm] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const e = await expenseService.getById(id);
      setExpense(e);
      expenseService.history(id).then(setHistory).catch(() => setHistory([]));
    } catch (err) {
      setError(serverMessage(err, 'Expense not found'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadPayFrom = () => {
    if (payFromOptions.length === 0) expenseService.getPayFromOptions().then(setPayFromOptions).catch(() => {});
  };
  const open = (kind, initial = {}) => {
    setForm(initial);
    setDialog(kind);
    if (kind === 'repeat') loadPayFrom();
  };
  const close = () => { setDialog(null); setForm({}); };
  const startPay = () => { setForm({ payFrom: '', paidAt: today(), chequeNumber: '', chequeDate: '' }); setShowPayForm(true); loadPayFrom(); };

  const run = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      close();
      setShowPayForm(false);
      setSnackbar(done);
      if (result === 'deleted') setTimeout(() => navigate('/accounting/expenses'), 500);
      else await load();
    } catch (err) {
      setError(serverMessage(err, 'The action failed'));
      close();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (!expense) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Expense not found'}</Alert>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/accounting/expenses')} sx={{ mt: 2, ...btnSx }}>
          Back to Expenses
        </Button>
      </Box>
    );
  }

  const s = expense.status;
  const isFine = expense.sourceType === 'WORK_ORDER_FINE' || expense.sourceType === 'PARTNER_FINE';
  const isUnpaidBill = s === 'APPROVED' && expense.expenseType === 'PAYABLE' && !expense.paidAt;
  const cheque = expense.cheque;
  const chequeIssued = cheque?.status === 'ISSUED';
  const canRefund = s === 'APPROVED' && expense.expenseType !== 'RETURN' && !isFine && !isUnpaidBill;
  const canRepeat = s === 'APPROVED' && expense.expenseType !== 'RETURN' && !isFine;
  const editable = !isFine && canWrite && (s === 'DRAFT' || s === 'PENDING_APPROVAL');
  const anyAction = !isFine && canWrite && (['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(s) || (s === 'REJECTED' && expense.expenseType !== 'RETURN'));
  const markPaidIsCheque = decodePayFrom(form.payFrom).paymentAccountType === 'CHEQUE';
  const payOption = payFromOptions.find((x) => x.value === form.payFrom);

  const groupedOptions = (opts) => {
    const by = {};
    opts.forEach((o) => { (by[o.group] = by[o.group] || []).push(o); });
    return Object.entries(by).flatMap(([g, list]) => [
      <ListSubheader key={`h-${g}`} sx={{ lineHeight: '28px', fontSize: fontSize.xs }}>{g}</ListSubheader>,
      ...list.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>),
    ]);
  };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/accounting/expenses')} sx={btnSx}>Back</Button>
          <Typography variant="h5" fontWeight={600} color="secondary.main">{expense.expenseNumber}</Typography>
          <StatusChip type="status" value={s} />
          {isUnpaidBill && <StatusChip tone="warning" label="Unpaid" />}
        </Box>
      </Box>

      {isFine && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {SOURCE_LABEL[expense.sourceType]} — created by the work order and read-only here. Not tax deductible.
          {expense.workOrderId && <> <Link component={RouterLink} to={`/work-orders/${expense.workOrderId}`}>Open the work order</Link></>}
        </Alert>
      )}
      {s === 'DRAFT' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Monthly draft waiting for review. Check the amount, then submit it for approval or skip this month.
        </Alert>
      )}

      {/* Detail Card */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography sx={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, mb: 0.5 }}>{expense.description}</Typography>
        {expense.payeeName && (
          <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', mb: 1.5 }}>
            {expense.payeeName}{expense.payeeType === 'EMPLOYEE' ? ' (team member)' : ''}
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Date">{formatDate(expense.date)}</DetailRow>
            {expense.salaryMonth && (
              <DetailRow label="Salary for">{new Date(`${String(expense.salaryMonth).slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</DetailRow>
            )}
            {(expense.payeeBankName || expense.payeeIban) && (
              <DetailRow label="Paid to">{[expense.payeeBankName, expense.payeeIban && `•••• ${String(expense.payeeIban).slice(-4)}`].filter(Boolean).join(' · ')}</DetailRow>
            )}
            {expense.category && (
              <DetailRow label="Category">{expense.category.group?.name ? <>{expense.category.group.name} &mdash; </> : null}{expense.category.name}</DetailRow>
            )}
            <DetailRow label="Type"><StatusChip type="expenseType" value={expense.expenseType} /></DetailRow>
            {expense.paymentAccount && <DetailRow label="Payment Account">{expense.paymentAccount.name || expense.paymentAccount.type}</DetailRow>}
            {expense.originalExpenseId && (
              <DetailRow label="Refund of"><Link component={RouterLink} to={`/accounting/expenses/${expense.originalExpenseId}`}>Original expense</Link></DetailRow>
            )}
            {expense.department && <DetailRow label="Department">{expense.department}</DetailRow>}
            {expense.reference && <DetailRow label="Reference">{expense.reference}</DetailRow>}
            {expense.remarks && <DetailRow label="Remarks">{expense.remarks}</DetailRow>}
            {expense.rejectionReason && <DetailRow label="Reason">{expense.rejectionReason}</DetailRow>}
            {expense.journalEntryId && <DetailRow label="Journal entry"><EntryLink id={expense.journalEntryId} /></DetailRow>}
            {expense.paidJournalEntryId && <DetailRow label="Payment entry"><EntryLink id={expense.paidJournalEntryId} /></DetailRow>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
              <DetailRow label="Amount">{formatCurrency(expense.amount)}</DetailRow>
              <DetailRow label="VAT">
                {formatCurrency(expense.vatAmount)}
                {expense.vatRecoverable && <Chip label="Recoverable" size="small" color="success" sx={{ ml: 1, fontSize: fontSize.xs, height: 20 }} />}
              </DetailRow>
              <Divider sx={{ my: 1 }} />
              <DetailRow label="Total">
                <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold }}>
                  {expense.expenseType === 'RETURN' ? '−' : ''}{formatCurrency(expense.total)}
                </Typography>
              </DetailRow>
            </Paper>
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {expense.sourceType === 'RECURRING_TEMPLATE' && <Chip label="Recurring (monthly)" size="small" color="info" sx={{ fontSize: fontSize.xs }} />}
              {expense.isTaxDeductible && <Chip label="Tax deductible" size="small" sx={{ fontSize: fontSize.xs }} />}
            </Box>
            <Box sx={{ mt: 1 }}>
              <ReceiptAttachment
                hasReceipt={!!expense.hasReceipt}
                filename={expense.receiptFilename}
                canEdit={canWrite}
                fetchUrl={() => expenseService.receiptUrl(id)}
                upload={(file) => expenseService.attachReceipt(id, file)}
                onUploaded={(updated) => { if (updated) setExpense(updated); setSnackbar('Receipt saved'); }}
                onError={setError}
              />
            </Box>
            {expense.paidAt && (
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 1 }}>Paid: {formatDate(expense.paidAt)}</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Cheque */}
      {cheque && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ ...sectionTitle, mb: 0 }}>Cheque</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {cheque.stale && <StatusChip tone="error" label="Stale" />}
              <StatusChip type="cheque" value={cheque.status} />
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Cheque number">{cheque.chequeNumber}</DetailRow>
              <DetailRow label="Cheque date">{formatDate(cheque.chequeDate)}</DetailRow>
              <DetailRow label="Bank">{cheque.bankAccountName}</DetailRow>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Amount">{formatCurrency(cheque.amount)}</DetailRow>
              {cheque.closedOn && <DetailRow label="Closed on">{formatDate(cheque.closedOn)}</DetailRow>}
              {cheque.closeReason && <DetailRow label="Reason">{cheque.closeReason}</DetailRow>}
            </Grid>
          </Grid>
          {chequeIssued && canWrite && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="contained" color="success" size="small" sx={btnSx} onClick={() => open('clear', { date: today() })}>Clear</Button>
              <Button color="error" size="small" sx={btnSx} onClick={() => open('bounce', { date: today(), reason: '' })}>Bounce</Button>
              <Button size="small" sx={btnSx} onClick={() => open('cancelCheque', { reason: '' })}>Cancel Cheque</Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={sectionTitle}>Actions</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {anyAction && s === 'DRAFT' && (
            <>
              <Button variant="contained" size="small" startIcon={<Send />} disabled={busy} sx={btnSx}
                onClick={() => run(() => expenseService.submit(id), 'Submitted for approval')}>Submit for Approval</Button>
              <Button size="small" startIcon={<SkipNext />} disabled={busy} sx={btnSx}
                onClick={() => run(() => expenseService.skip(id), 'Skipped this month')}>Skip This Month</Button>
            </>
          )}
          {anyAction && s === 'PENDING_APPROVAL' && (
            <>
              <Button variant="contained" color="success" size="small" startIcon={<Check />} disabled={busy} sx={btnSx}
                onClick={() => run(() => expenseService.approve(id), 'Expense approved')}>Approve</Button>
              <Button variant="contained" color="error" size="small" startIcon={<Close />} sx={btnSx}
                onClick={() => open('reject', { reason: '' })}>Reject</Button>
            </>
          )}
          {editable && (
            <>
              <Button variant="text" size="small" startIcon={<Edit />} sx={btnSx} onClick={() => navigate(`/accounting/expenses/${id}/edit`)}>Edit</Button>
              <Button variant="text" color="error" size="small" startIcon={<Delete />} sx={btnSx} onClick={() => open('delete')}>Delete</Button>
            </>
          )}
          {anyAction && isUnpaidBill && (!showPayForm ? (
            <Button variant="contained" size="small" startIcon={<Payment />} sx={btnSx} onClick={startPay}>Mark as Paid</Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Pay from</InputLabel>
                <Select value={form.payFrom || ''} label="Pay from" onChange={(e) => setForm({ ...form, payFrom: e.target.value })}>
                  {groupedOptions(payFromOptions)}
                </Select>
              </FormControl>
              <TextField label="Paid on" type="date" size="small" value={form.paidAt || ''} sx={{ width: 160 }}
                onChange={(e) => setForm({ ...form, paidAt: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
              {markPaidIsCheque && (
                <>
                  <TextField label="Cheque #" size="small" sx={{ width: 120 }} value={form.chequeNumber || ''} onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })} />
                  <TextField label="Cheque date" type="date" size="small" sx={{ width: 160 }} value={form.chequeDate || ''}
                    onChange={(e) => setForm({ ...form, chequeDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                </>
              )}
              <Button variant="contained" size="small" sx={btnSx}
                disabled={busy || !form.payFrom || !form.paidAt || (markPaidIsCheque && (!(form.chequeNumber || '').trim() || !form.chequeDate))}
                onClick={() => run(() => expenseService.markPaid(id, {
                  ...decodePayFrom(form.payFrom),
                  paidAt: form.paidAt,
                  ...(markPaidIsCheque ? { chequeNumber: form.chequeNumber.trim(), chequeDate: form.chequeDate } : {}),
                }), 'Marked as paid')}>Confirm</Button>
              <Button variant="text" size="small" sx={btnSx} onClick={() => setShowPayForm(false)}>Cancel</Button>
              {payOption && ['Bank', 'Cash'].includes(payOption.group) && payOption.balance != null && Number(expense.total) > Number(payOption.balance) && (
                <Typography sx={{ fontSize: fontSize.xs, color: 'warning.main', width: '100%' }}>
                  This will make {payOption.label} negative (balance {formatCurrency(payOption.balance)})
                </Typography>
              )}
            </Box>
          ))}
          {anyAction && canRefund && (
            <Button size="small" startIcon={<Replay />} sx={btnSx} onClick={() => navigate(`/accounting/expenses/new?refundOf=${id}`)}>Record Refund</Button>
          )}
          {anyAction && canRepeat && (
            <Button size="small" startIcon={<Repeat />} sx={btnSx}
              onClick={() => open('repeat', { dayOfMonth: Math.min(28, Number(String(expense.date).slice(8, 10)) || 1), payFrom: '' })}>Repeat Monthly</Button>
          )}
          {anyAction && s === 'APPROVED' && (
            <Button size="small" color="error" startIcon={<Undo />} sx={btnSx} onClick={() => open('reverse', { reason: '' })}>Reverse</Button>
          )}
          {anyAction && s === 'REJECTED' && (
            <Button variant="contained" size="small" startIcon={<ContentCopy />} sx={btnSx}
              onClick={() => navigate(`/accounting/expenses/new?copyOf=${id}`)}>Copy as New</Button>
          )}
          {!anyAction && (
            <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>
              {isFine ? 'Managed from the work order. No actions available.' : s === 'REVERSED' ? 'This expense was reversed. No actions available.'
                : !canWrite ? 'You have view access only. No actions available.' : 'This expense is fully processed. No actions available.'}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Audit Trail */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography sx={sectionTitle}>Audit Trail</Typography>
        {history.length === 0 && <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>No history recorded.</Typography>}
        {history.map((h) => (
          <Box key={h.id} sx={{ display: 'flex', gap: 2, py: 0.5 }}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', minWidth: 130 }}>{formatDateTime(h.occurredAt)}</Typography>
            <Typography sx={{ fontSize: fontSize.sm }}>
              {String(h.action || '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
              {h.actorName ? ` by ${h.actorName}` : ''}
              {h.details?.reason ? ` — ${h.details.reason}` : ''}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Reject / reverse / cancel cheque */}
      <Dialog open={['reject', 'reverse', 'cancelCheque'].includes(dialog)} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{{ reject: 'Reject Expense', reverse: 'Reverse Expense', cancelCheque: 'Cancel Cheque' }[dialog]}</DialogTitle>
        <DialogContent>
          {dialog === 'reverse' && (
            <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', mb: 1 }}>
              Posts a reversing entry. Refused while a refund is open or when the expense was paid by a cheque that is still live.
            </Typography>
          )}
          <TextField label={dialog === 'reject' ? 'Rejection Reason' : 'Reason'} value={form.reason || ''}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth multiline rows={3} sx={{ mt: 1 }}
            placeholder={dialog === 'reject' ? 'Why is this expense being rejected?' : undefined} />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || !(form.reason || '').trim()} sx={{ textTransform: 'none' }}
            onClick={() => {
              const reason = form.reason.trim();
              if (dialog === 'reject') run(() => expenseService.reject(id, reason), 'Expense rejected');
              if (dialog === 'reverse') run(() => expenseService.reverse(id, reason), 'Expense reversed');
              if (dialog === 'cancelCheque') run(() => expenseService.cancelCheque(cheque.id, reason), 'Cheque cancelled — a paid-now expense is reversed, a bill becomes unpaid again');
            }}>
            {dialog === 'reject' ? 'Reject' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear / bounce cheque */}
      <Dialog open={dialog === 'clear' || dialog === 'bounce'} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog === 'clear' ? 'Cheque Cleared' : 'Cheque Bounced'}</DialogTitle>
        <DialogContent>
          <TextField label="Date" type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })}
            fullWidth size="small" sx={{ mt: 1 }} InputLabelProps={{ shrink: true }} />
          {dialog === 'bounce' && (
            <TextField label="Reason" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} fullWidth size="small" sx={{ mt: 2 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={busy || !form.date || (dialog === 'bounce' && !(form.reason || '').trim())} sx={{ textTransform: 'none' }}
            onClick={() => (dialog === 'clear'
              ? run(() => expenseService.clearCheque(cheque.id, form.date), 'Cheque cleared')
              : run(() => expenseService.bounceCheque(cheque.id, form.date, form.reason.trim()), 'Cheque bounced — a paid-now expense is reversed, a bill becomes unpaid again'))}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repeat monthly → recurring template */}
      <Dialog open={dialog === 'repeat'} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>Repeat Monthly</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', mb: 1.5 }}>
            Each month on this day a draft copy waits in Pending Review. If this month&apos;s day has already passed, a draft for
            this month also appears tonight — skip it there, since this expense already covers the month.
          </Typography>
          <TextField label="Day of month (1–28)" type="number" value={form.dayOfMonth || ''} size="small" fullWidth
            onChange={(e) => setForm({ ...form, dayOfMonth: e.target.value })} inputProps={{ min: 1, max: 28 }} />
          {expense.expenseType === 'PAID' && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Pay from</InputLabel>
              <Select value={form.payFrom || ''} label="Pay from" onChange={(e) => setForm({ ...form, payFrom: e.target.value })}>
                {groupedOptions(payFromOptions.filter((o) => o.group !== 'Cheque'))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" sx={{ textTransform: 'none' }}
            disabled={busy || !(Number(form.dayOfMonth) >= 1 && Number(form.dayOfMonth) <= 28) || (expense.expenseType === 'PAID' && !form.payFrom)}
            onClick={() => run(() => expenseService.createTemplate({
              categoryId: expense.category?.id,
              description: expense.description,
              payeeName: expense.payeeId ? undefined : expense.payeeName,
              payeeId: expense.payeeId || undefined,
              amount: expense.amount,
              vatAmount: expense.vatAmount,
              vatRecoverable: expense.vatRecoverable,
              expenseType: expense.expenseType,
              isTaxDeductible: expense.isTaxDeductible,
              dayOfMonth: Number(form.dayOfMonth),
              ...(expense.expenseType === 'PAID' ? decodePayFrom(form.payFrom) : {}),
            }), 'Monthly template created')}>
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={dialog === 'delete'} onClose={close}>
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {expense.expenseNumber}?</Typography>
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium }}>{expense.description}</Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{formatCurrency(expense.total)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} sx={{ textTransform: 'none' }}
            onClick={() => run(async () => { await expenseService.remove(id); return 'deleted'; }, 'Expense deleted')}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default ExpenseDetail;
